import {mixedSettlementLearningBlocked95,MIXED_SETTLEMENT_LEARNING_REASON95} from "./mixedSettlementLearning95";
import type { DbExecutor } from "./executor";
import type { ProposedCostRepository } from "./repositories/proposedCostRepository";
import type { DesignRepository } from "./repositories/designRepository";
import type { CategoryRepository } from "./repositories/categoryRepository";
import type { ReconciliationService } from "./reconciliationService";
import type { BidService } from "./bidService";
import type { CalibrationService, HistoricalObservation, RatePrediction } from "./calibrationService";
import { resolveCategory } from "../engine/categoryBudget";
import { reviewCompletion, type CompletionFeatureInput, type CompletionReview } from "../engine/completion";
import { categoriesById, effectiveBucket, expenseTotalCents } from "../engine/financials";
import { tradeById, TRADES, type TradeId } from "../domain/trade";
import { rateUnitWords, type RateSurface } from "../domain/rateUnit";
import { effectiveFeatureQuantity, type FeatureGeometryBinding, type QuantityUnit, type ProjectFeature } from "../domain/feature";
import { surfaceOfBinding } from "../domain/rateUnit";
import type { Cents } from "../domain/money";

/**
 * CLOSING OUT A RENOVATION, and what the company learns from it (spec §91).
 *
 * The read model behind the Completion Review, plus the two decisions it
 * records: whether a piece of work is finished (project_features
 * .work_status — Phase 9C's column, the ONE completion truth, written
 * through the service that already owns it) and whether the renovation as a
 * whole has been closed out (projects.renovation_completed_at).
 *
 * WHAT THIS SERVICE MAY NEVER DO, and does not:
 *
 *   - create, move, re-attribute or infer a single cent;
 *   - change a Proposed figure, a quote, or anything a report has printed;
 *   - mark work complete on its own, however strong the evidence looks.
 *
 * Completion tells estimating which results are FINAL. That is its whole
 * job, and everything else about the project stays exactly where it was.
 */

export interface CompletionState {
  /** Null while the renovation is still open. */
  renovationCompletedAt: string | null;
}

export interface LearnedRate {
  featureDefinitionId: string;
  /** "Install LVP" — the kind of work. */
  label: string;
  /** What this project actually paid per unit, x100 cents. */
  actualUnitCostCentsX100: number;
  unitWords: string;
  quantityX100: number;
  actualCents: Cents;
  /** What the profile was estimating with before this project taught it. */
  previousEstimateCents: Cents | null;
  /** What it will estimate with now. */
  newEstimateCents: Cents | null;
  sampleSize: number;
  featureId: string;
  excluded: { reasonCode: string; reason: string } | null;
}

export interface LearningSummary {
  profileId: string | null;
  profileName: string | null;
  /** One row per observation this project contributes. */
  learned: LearnedRate[];
  /**
    * Completed work that teaches nothing, and why. Grouped by kind of work
    * and reason: the walkthrough printed "Replace Baseboard" four times
    * with the same sentence, which is noise, not information.
    */
  notUsed: Array<{ featureId: string; name: string; reason: string; count: number }>;
  learnedCount: number;
}

export class CompletionService {
  constructor(
    private readonly db: DbExecutor,
    private readonly proposedCosts: ProposedCostRepository,
    private readonly designs: DesignRepository,
    private readonly categories: CategoryRepository,
    private readonly reconciliation: ReconciliationService,
    private readonly bids: BidService,
    private readonly calibration: CalibrationService,
  ) {}

  /* ---------------- the review (spec §91-3) ---------------- */

  async review(projectId: string): Promise<CompletionReview> {
    const designId = await this.designs.getActiveDesignId(projectId);
    const [featureRows, decisionRows, tradeRows, roomRows, lines, events, expenseCategories, taskRows, quotes, bidModel, exclusions, mixedLearningBlocked] =
      await Promise.all([
        this.db.select<{
          id: string; origin_id: string | null; feature_definition_id: string | null; name: string;
          action: string; room_id: string | null; work_status: string | null;
          quantity_x100: number | null; quantity_geometry_x100: number | null;
          quantity_source: ProjectFeature["quantitySource"];
          quantity_unit: string | null; quantity_geometry_binding: string | null; finish_key: string | null;
        }>(
          `SELECT id, origin_id, feature_definition_id, name, action, room_id, work_status,
                  quantity_x100, quantity_geometry_x100, quantity_unit, quantity_geometry_binding, quantity_source, finish_key
             FROM project_features
            WHERE project_id = ? AND archived_at IS NULL AND (design_id IS NULL OR design_id = ?)`,
          [projectId, designId],
        ),
        this.db.select<{ root_id: string; trade_override: string | null }>(
          "SELECT root_id, trade_override FROM bid_decisions WHERE project_id = ? AND (design_id IS ? OR design_id = ?)",
          [projectId, designId, designId],
        ),
        this.db.select<{ trade_id: string; combine_with: string | null }>(
          "SELECT trade_id, combine_with FROM bid_trade_settings WHERE project_id = ? AND (design_id IS ? OR design_id = ?)",
          [projectId, designId, designId],
        ),
        this.db.select<{ id: string; name: string; floor_name: string }>(
          `SELECT r.id, r.name, f.name AS floor_name
             FROM rooms r JOIN floors f ON f.id = r.floor_id
            WHERE f.project_id = ? AND r.archived_at IS NULL`,
          [projectId],
        ),
        this.proposedCosts.listForDesign(projectId, designId),
        this.reconciliation.eventsForProject(projectId),
        this.categories.listAll(),
        this.db.select<{ feature_id: string; status: string; actual_finish: string | null }>(
          "SELECT feature_id, status, actual_finish FROM project_tasks WHERE project_id = ? AND archived_at IS NULL AND feature_id IS NOT NULL",
          [projectId],
        ),
        this.bids.listQuotes(projectId),
        this.bids.model(projectId, designId),
        this.calibration.observationExclusions(projectId),
        mixedSettlementLearningBlocked95(this.db,[projectId]),
      ]);

    const decisions = new Map<string, TradeId>();
    for (const row of decisionRows) if (row.trade_override) decisions.set(row.root_id, row.trade_override as TradeId);
    const combines = new Map<TradeId, TradeId>();
    for (const row of tradeRows) {
      if (row.combine_with && row.combine_with !== row.trade_id) combines.set(row.trade_id as TradeId, row.combine_with as TradeId);
    }

    const roomLabels = new Map(roomRows.map((r) => [r.id, r.floor_name ? `${r.floor_name} → ${r.name}` : r.name]));
    const proposedByFeature = new Map<string, Cents>();
    for (const line of lines) {
      if (line.archivedAt !== null || line.featureId === null) continue;
      proposedByFeature.set(line.featureId, (proposedByFeature.get(line.featureId) ?? 0) + line.amountCents);
    }

    /*
     * Actual money resolved to a work item through the accounting's own
     * links, never guessed from timing or category. Two figures are kept
     * apart deliberately: the one SHOWN includes sales tax, because that is
     * what every other screen shows, and the one LEARNED excludes it,
     * because the estimate adds estimating tax of its own (§9E).
     */
    const byId = categoriesById(expenseCategories);
    const lineFeature = new Map(lines.map((l) => [l.id, l.featureId]));
    const actualByFeature = new Map<string, Cents>();
    const beforeTaxByFeature = new Map<string, Cents>();
    const voidedFeatures = new Set<string>();
    for (const event of events) {
      const featureId =
        event.linkedFeatureId ?? (event.linkedProposedLineId ? (lineFeature.get(event.linkedProposedLineId) ?? null) : null);
      if (featureId === null) continue;
      if (event.voidedAt !== null) {
        voidedFeatures.add(featureId);
        continue;
      }
      if (effectiveBucket(event, byId) !== "renovation") continue;
      actualByFeature.set(featureId, (actualByFeature.get(featureId) ?? 0) + expenseTotalCents(event));
      beforeTaxByFeature.set(featureId, (beforeTaxByFeature.get(featureId) ?? 0) + event.amountCents);
    }

    const tasks = new Map<string, { total: number; finished: number }>();
    for (const row of taskRows) {
      const entry = tasks.get(row.feature_id) ?? { total: 0, finished: 0 };
      entry.total += 1;
      if (row.status === "complete" || row.actual_finish !== null) entry.finished += 1;
      tasks.set(row.feature_id, entry);
    }

    // Which work a SELECTED quote claims — through the §68/§84 bid scopes,
    // the same claiming machinery the budget uses. Evidence, never money.
    const quotedBy = new Map<string, string>();
    const scopeFeatures = new Map(bidModel.scopes.map((s) => [s.key, s.featureIds]));
    for (const quote of quotes) {
      if (quote.selectedAt === null) continue;
      for (const key of quote.coverage) {
        for (const featureId of scopeFeatures.get(key) ?? []) quotedBy.set(featureId, quote.contractor);
      }
    }

    const exclusionByFeature = new Map(exclusions.map((e) => [e.featureId, { reasonCode: e.reasonCode, reason: e.reason }]));

    const inputs: CompletionFeatureInput[] = featureRows.map((row) => {
      const binding = (row.quantity_geometry_binding as FeatureGeometryBinding | null) ?? null;
      const quantity = effectiveFeatureQuantity({quantityX100:row.quantity_x100,quantityUnit:row.quantity_unit as QuantityUnit|null,quantitySource:row.quantity_source,quantityGeometryBinding:binding,quantityGeometryX100:row.quantity_geometry_x100});
      const finishSurface: RateSurface | null = row.finish_key?.startsWith("walls:")
        ? "wall"
        : row.finish_key?.startsWith("ceiling:")
          ? "ceiling"
          : row.finish_key?.startsWith("floor:")
            ? "floor"
            : null;
      const tradeId = resolveCategory(
        { featureDefinitionId: row.feature_definition_id, action: row.action, rootId: row.origin_id ?? row.id },
        decisions,
        combines,
      );
      const trade = tradeById(tradeId);
      return {
        featureId: row.id,
        featureDefinitionId: row.feature_definition_id,
        name: row.name,
        action: row.action,
        roomId: row.room_id,
        locationLabel: row.room_id ? (roomLabels.get(row.room_id) ?? "") : "",
        categoryId: tradeId,
        categoryDisplayName: trade?.displayName ?? tradeId,
        categorySortOrder: TRADES.findIndex((t) => t.id === tradeId),
        workStatus: row.work_status,
        proposedCents: proposedByFeature.get(row.id) ?? 0,
        actualCents: actualByFeature.get(row.id) ?? 0,
        actualBeforeTaxCents: beforeTaxByFeature.get(row.id) ?? 0,
        hasVoidedMoney: voidedFeatures.has(row.id) && (actualByFeature.get(row.id) ?? 0) <= 0,
        quantityX100: quantity.valueX100,
        quantityUnit: quantity.unit,
        surface: finishSurface ?? surfaceOfBinding(binding),
        scheduleTaskCount: tasks.get(row.id)?.total ?? 0,
        scheduleFinishedCount: tasks.get(row.id)?.finished ?? 0,
        quotedByContractor: quotedBy.get(row.id) ?? null,
        exclusion: mixedLearningBlocked.has(row.id)?MIXED_SETTLEMENT_LEARNING_REASON95:exclusionByFeature.get(row.id) ?? null,
      };
    });

    return reviewCompletion(inputs);
  }

  /* ---------------- the decisions ---------------- */

  /**
   * Mark work finished, or unfinished (spec §91-5). Bulk is the same
   * decision applied to the items the user selected — there is no second
   * write path, and nothing about the money changes either way.
   */
  async setComplete(featureIds: readonly string[], complete: boolean): Promise<void> {
    if (featureIds.length === 0) return;
    await this.db.transaction(async () => {
      for (const featureId of featureIds) {
        await this.reconciliation.setWorkStatus(featureId, complete ? "complete" : null);
      }
    });
  }

  /** Every work item of one trade, so a finished trade is one action. */
  async completeTrade(projectId: string, categoryId: string, complete: boolean): Promise<number> {
    const review = await this.review(projectId);
    const trade = review.trades.find((t) => t.categoryId === categoryId);
    if (!trade) return 0;
    const ids = trade.items.flatMap((i) => i.featureIds);
    await this.setComplete(ids, complete);
    return ids.length;
  }

  async state(projectId: string): Promise<CompletionState> {
    const rows = await this.db.select<{ renovation_completed_at: string | null }>(
      "SELECT renovation_completed_at FROM projects WHERE id = ?",
      [projectId],
    );
    return { renovationCompletedAt: rows[0]?.renovation_completed_at ?? null };
  }

  /**
   * THE RENOVATION IS FINISHED (spec §91-6). A decision, not a
   * calculation: a project may legitimately close with items that teach
   * nothing, so nothing is required of the user beyond having looked. It is
   * reversible, and it moves no money.
   */
  async completeRenovation(projectId: string): Promise<void> {
    await this.db.execute("UPDATE projects SET renovation_completed_at = ?, updated_at = ? WHERE id = ?", [
      new Date().toISOString(),
      new Date().toISOString(),
      projectId,
    ]);
  }

  async reopenRenovation(projectId: string): Promise<void> {
    await this.db.execute("UPDATE projects SET renovation_completed_at = NULL, updated_at = ? WHERE id = ?", [
      new Date().toISOString(),
      projectId,
    ]);
  }

  /* ---------------- what was learned (spec §91-7) ---------------- */

  /**
   * WHAT THIS PROJECT TAUGHT THE ESTIMATOR.
   *
   * One row per observation the project contributes, each with the figure
   * the profile was estimating with BEFORE this project counted and the
   * figure it estimates with now — so the user can see the effect of their
   * own finished house rather than being told that learning happened.
   *
   * Deterministic arithmetic all the way down (§90's base + median model);
   * nothing here is a model, a prediction engine or an inference.
   */
  async learningSummary(projectId: string): Promise<LearningSummary> {
    const [profile, review] = await Promise.all([this.calibration.projectProfile(projectId), this.review(projectId)]);
    const members = review.trades.flatMap((t) => t.items).flatMap((i) => i.members);
    const grouped = new Map<string, { featureId: string; name: string; reason: string; count: number }>();
    for (const m of members) {
      if (m.state !== "complete" || m.learning.willTeach) continue;
      const key = m.name + "|" + m.learning.reason;
      const found = grouped.get(key);
      if (found) found.count += 1;
      else grouped.set(key, { featureId: m.featureId, name: m.name, reason: m.learning.reason, count: 1 });
    }
    const notUsed = [...grouped.values()].sort((a, b) => b.count - a.count || a.name.localeCompare(b.name));

    if (profile === null) {
      return { profileId: null, profileName: null, learned: [], notUsed, learnedCount: 0 };
    }

    const [observations, before, after, definitionRows] = await Promise.all([
      this.calibration.observations(),
      // What the profile estimated with when this project did not count…
      this.calibration.predictions(profile.id, { excludeProjectId: projectId }),
      // …and what it estimates with now that it does.
      this.calibration.predictions(profile.id),
      this.db.select<{ id: string; name: string }>("SELECT id, display_name AS name FROM feature_definitions"),
    ]);
    const names = new Map(definitionRows.map((r) => [r.id, r.name]));
    const mine = observations.filter((o) => o.projectId === projectId);
    const rateFor = (list: RatePrediction[], observation: HistoricalObservation): RatePrediction | undefined =>
      list.find((p) => p.rate.featureDefinitionId === observation.featureDefinitionId);

    const learned: LearnedRate[] = mine.map((observation) => {
      const previous = rateFor(before, observation);
      const current = rateFor(after, observation);
      return {
        featureDefinitionId: observation.featureDefinitionId,
        label: names.get(observation.featureDefinitionId) ?? observation.featureName,
        actualUnitCostCentsX100: observation.unitCostCentsX100,
        unitWords: rateUnitWords(
          observation.unit === "sqft" ? "sqft" : observation.unit === "lf" ? "lf" : observation.unit === "square" ? "square" : "each",
          observation.surface,
        ),
        quantityX100: observation.quantityX100,
        actualCents: observation.actualCents,
        previousEstimateCents: previous ? previous.predictedCents : null,
        newEstimateCents: current ? current.predictedCents : null,
        sampleSize: current?.sampleSize ?? 0,
        featureId: observation.featureId,
        excluded: observation.excluded,
      };
    });
    learned.sort((a, b) => b.actualCents - a.actualCents);
    return {
      profileId: profile.id,
      profileName: profile.name,
      learned,
      notUsed,
      learnedCount: learned.filter((l) => l.excluded === null).length,
    };
  }
}
